import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { FeedbackType } from '../../types/feedback'
import type { TabKey } from './lastUsedTab'

interface FeedbackUIState {
  open: boolean
  initialTab: TabKey
  presetType?: FeedbackType
}

interface FeedbackUIContextValue extends FeedbackUIState {
  openDrawer: (opts?: { tab?: TabKey; presetType?: FeedbackType }) => void
  closeDrawer: () => void
}

const FeedbackUIContext = createContext<FeedbackUIContextValue | undefined>(undefined)

interface FeedbackUIProviderProps {
  children: ReactNode
}

export function FeedbackUIProvider({ children }: FeedbackUIProviderProps) {
  const [state, setState] = useState<FeedbackUIState>({
    open: false,
    initialTab: 'submit',
    presetType: undefined,
  })

  const openDrawer = useCallback(
    (opts?: { tab?: TabKey; presetType?: FeedbackType }) => {
      setState({
        open: true,
        initialTab: opts?.tab || 'submit',
        presetType: opts?.presetType,
      })
    },
    [],
  )

  const closeDrawer = useCallback(() => {
    setState(prev => ({ ...prev, open: false }))
  }, [])

  // Deep-link: `?help=mine` (the feedback-reply notification's action_url)
  // opens the drawer on its "My items" tab, then strips the param so a
  // refresh / back-navigation doesn't re-open it.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('help') !== 'mine') return
    openDrawer({ tab: 'mine' })
    const next = new URLSearchParams(searchParams)
    next.delete('help')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams, openDrawer])

  const value = useMemo<FeedbackUIContextValue>(
    () => ({ ...state, openDrawer, closeDrawer }),
    [state, openDrawer, closeDrawer],
  )

  return <FeedbackUIContext.Provider value={value}>{children}</FeedbackUIContext.Provider>
}

export function useFeedbackUI(): FeedbackUIContextValue {
  const ctx = useContext(FeedbackUIContext)
  if (!ctx) {
    throw new Error('useFeedbackUI must be used within FeedbackUIProvider')
  }
  return ctx
}
